import { authFetch } from '../utils/authFetch';
import { NAVIGATION_PURGE_EVENT } from '../navigation/sessionStore';
/**
 * Lightweight cached fetch helpers for reception-specific APIs.
 * Consolidates duplicated fetch logic and keeps hot endpoints cached briefly
 * to minimise redundant network requests while preserving behaviour.
 */


interface CacheEntry<T> {
  value: T;
  expires: number;
}

const cacheStore = new Map<string, CacheEntry<unknown>>();
const inflightStore = new Map<string, Promise<unknown>>();
let cacheGeneration: object = {};

const GROUPS_TTL_MS = 90_000;      // 90 seconds (increased from 60)
const DASHBOARD_TTL_MS = 20_000;   // 20 seconds (increased from 15)
const COURSES_TTL_MS = 15 * 60_000; // 15 minutes (increased from 10)
const BRANCHES_TTL_MS = 10 * 60_000; // 10 minutes (increased from 5)
const STUDENTS_TTL_MS = 8_000;      // 8 seconds (increased from 4)

interface CacheOptions {
  ttl: number;
  force?: boolean;
  keySuffix?: string;
}

const now = () => Date.now();
const makeKey = (resource: string, suffix?: string) => (suffix ? `${resource}:${suffix}` : resource);

async function runCached<T>(resource: string, fetcher: () => Promise<T>, options: CacheOptions): Promise<T> {
  const key = makeKey(resource, options.keySuffix);
  if (!options.force) {
    const entry = cacheStore.get(key) as CacheEntry<T> | undefined;
    if (entry && entry.expires > now()) {
      return entry.value;
    }
    const existing = inflightStore.get(key) as Promise<T> | undefined;
    if (existing) {
      return existing;
    }
  }

  const requestGeneration = cacheGeneration;
  const promise = (async () => {
    const value = await fetcher();
    if (requestGeneration === cacheGeneration) {
      cacheStore.set(key, { value, expires: now() + options.ttl });
    }
    return value;
  })();

  inflightStore.set(key, promise as Promise<unknown>);
  try {
    return await promise;
  } finally {
    if (inflightStore.get(key) === promise) {
      inflightStore.delete(key);
    }
  }
}

export interface ReceptionGroup {
  id: number;
  code: string;
  name: string;
}

export interface ReceptionDashboardStats {
  pending?: number;
  approved?: number;
  ready?: number;
  groups?: number;
  instructors?: number;
  inactive?: number;
  flags?: Record<string, number>;
  courses?: Record<string, number>;
  [key: string]: unknown;
}

export interface ReceptionStudentListParams {
  page?: number;
  pageSize?: number;
  category?: string;
  query?: string;
  flag?: string;
  course?: string;
  fields?: string;
  extra?: Record<string, string | number | null | undefined>;
}

export interface ReceptionStudentListResponse<T> {
  students: T[];
  page?: number;
  page_size?: number;
  pages?: number;
  total?: number;
  [key: string]: unknown;
}

export async function fetchReceptionGroups(options: { force?: boolean; ttl?: number } = {}): Promise<ReceptionGroup[]> {
  const { force, ttl = GROUPS_TTL_MS } = options;
  return runCached('groups', async () => {
    const res = await authFetch('/reception/api/groups');
    if (!res.ok) {
      if(res.status === 401) { throw new Error('UNAUTHORIZED'); }
      throw new Error(`HTTP ${res.status}`);
    }
    const payload = await res.json();
    const raw = Array.isArray(payload) ? payload : (payload?.groups ?? []);
    type RawGroup = { id: number; code?: string; name?: string; group_code?: string; group_name?: string };
    return (raw as RawGroup[]).map((g) => ({
      id: g.id,
      code: g.code || g.group_code || '',
      name: g.name || g.group_name || g.group_code || '',
    }));
  }, { ttl, force });
}

export async function fetchReceptionDashboardStats(options: { force?: boolean; ttl?: number } = {}): Promise<ReceptionDashboardStats> {
  const { force, ttl = DASHBOARD_TTL_MS } = options;
  return runCached('dashboard', async () => {
    const res = await authFetch('/reception/api/dashboard');
    if (!res.ok) {
      if(res.status === 401) { throw new Error('UNAUTHORIZED'); }
      throw new Error(`HTTP ${res.status}`);
    }
    const payload = await res.json();
    return payload ?? {};
  }, { ttl, force });
}

export async function fetchReceptionCourses(options: { force?: boolean; ttl?: number; courseType?: 'language' | 'non_language' | '' } = {}): Promise<string[]> {
  const { force, ttl = COURSES_TTL_MS, courseType = '' } = options;
  const qs = new URLSearchParams();
  if (courseType) qs.set('course_type', courseType);
  const keySuffix = qs.toString();
  return runCached('courses', async () => {
    const res = await authFetch(`/reception/api/courses${keySuffix ? `?${keySuffix}` : ''}`);
    if (!res.ok) {
      if(res.status === 401) { throw new Error('UNAUTHORIZED'); }
      throw new Error(`HTTP ${res.status}`);
    }
    const payload = await res.json();
    const list: unknown[] = Array.isArray(payload?.courses) ? payload.courses as unknown[] : Array.isArray(payload) ? payload as unknown[] : [];
    return list
      .map((c: unknown): string => String(c))
      .filter((c: string) => c.length > 0);
  }, { ttl, force, keySuffix });
}

export async function fetchReceptionBranches(options: { force?: boolean; ttl?: number } = {}): Promise<string[]> {
  const { force, ttl = BRANCHES_TTL_MS } = options;
  return runCached('branches', async () => {
    const res = await authFetch('/reception/api/branches');
    if (!res.ok) {
      if (res.status === 401) {
        throw new Error('UNAUTHORIZED');
      }
      throw new Error(`HTTP ${res.status}`);
    }
    const payload = await res.json();
    const list: unknown[] = Array.isArray(payload?.branches) ? payload.branches as unknown[] : Array.isArray(payload) ? payload as unknown[] : [];
    return list
      .map((b: unknown): string => String(b))
      .filter((b: string) => b.length > 0);
  }, { ttl, force });
}

export async function fetchReceptionStudentsList<T>(params: ReceptionStudentListParams, options: { force?: boolean; ttl?: number } = {}): Promise<ReceptionStudentListResponse<T>> {
  const { force, ttl = STUDENTS_TTL_MS } = options;
  const search = new URLSearchParams();
  const page = Math.max(1, params.page ?? 1);
  const pageSize = Math.min(200, Math.max(1, params.pageSize ?? 50));
  search.set('page', String(page));
  search.set('page_size', String(pageSize));
  const fields = params.fields ?? 'basic';
  if (fields) {
    search.set('fields', fields);
  }
  if (params.category) {
    search.set('category', params.category);
  }
  if (params.query) {
    search.append('q', params.query);
  }
  if (params.flag) {
    search.append('flag', params.flag);
  }
  if (params.course) {
    search.append('course', params.course);
  }
  if (params.extra) {
    for (const [k, v] of Object.entries(params.extra)) {
      if (v === undefined || v === null) continue;
      search.append(k, String(v));
    }
  }
  const queryString = search.toString();
  return runCached('students', async () => {
    const res = await authFetch(`/reception/api/students?${queryString}`);
    if (!res.ok) {
      if(res.status === 401) { throw new Error('UNAUTHORIZED'); }
      throw new Error(`HTTP ${res.status}`);
    }
    return (await res.json()) as ReceptionStudentListResponse<T>;
  }, { ttl, force, keySuffix: queryString });
}

export function invalidateReceptionResource(resource?: 'groups' | 'dashboard' | 'courses' | 'branches' | 'students', suffix?: string): void {
  cacheGeneration = {};
  if (!resource) {
    cacheStore.clear();
    inflightStore.clear();
    return;
  }
  if (suffix) {
    const key = makeKey(resource, suffix);
    cacheStore.delete(key);
    inflightStore.delete(key);
    return;
  }
  for (const key of Array.from(cacheStore.keys())) {
    if (key === resource || key.startsWith(`${resource}:`)) {
      cacheStore.delete(key);
    }
  }
  for (const key of Array.from(inflightStore.keys())) {
    if (key === resource || key.startsWith(`${resource}:`)) {
      inflightStore.delete(key);
    }
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener(NAVIGATION_PURGE_EVENT, () => {
    invalidateReceptionResource();
  });
}
